import { useEffect, useRef, useCallback } from 'react';

const useTabNotification = (messageCount = 0, isActive = true) => {
  const originalTitleRef = useRef(document.title);
  const notificationTimeoutRef = useRef(null);

  // Update tab title with message count
  const updateTabTitle = useCallback((count) => {
    if (count > 0 && !isActive) {
      document.title = `(${count}) ${originalTitleRef.current}`;
    } else {
      document.title = originalTitleRef.current;
    }
  }, [isActive]);

  // Clear notifications when user focuses on tab
  const clearNotifications = useCallback(() => {
    document.title = originalTitleRef.current;
    
    // Also clear any blinking effect
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
  }, []);

  // Handle visibility change
  useEffect(() => {
    // Capture the original title value inside the effect
    const originalTitle = originalTitleRef.current;
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab is active, clear notifications
        clearNotifications();
      } else {
        // Tab is inactive, update with current message count
        updateTabTitle(messageCount);
      }
    };

    const handleFocus = () => {
      clearNotifications();
    };

    const handleBlur = () => {
      updateTabTitle(messageCount);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      
      // Restore original title on cleanup using captured value
      document.title = originalTitle;
      
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [messageCount, updateTabTitle, clearNotifications]);

  // Update title when message count changes and tab is not active
  useEffect(() => {
    if (document.hidden && messageCount > 0) {
      updateTabTitle(messageCount);
      
      // Add blinking effect for new messages
      let blinkCount = 0;
      const maxBlinks = 6;
      
      const blink = () => {
        if (blinkCount < maxBlinks && document.hidden) {
          document.title = blinkCount % 2 === 0 
            ? `📬 ${originalTitleRef.current}` 
            : `(${messageCount}) ${originalTitleRef.current}`;
          
          blinkCount++;
          notificationTimeoutRef.current = setTimeout(blink, 1000);
        } else {
          // Stop blinking and show final count
          updateTabTitle(messageCount);
        }
      };
      
      // Start blinking after a short delay
      notificationTimeoutRef.current = setTimeout(blink, 500);
    }
  }, [messageCount, updateTabTitle]);

  return {
    clearNotifications,
    updateTabTitle
  };
};

export default useTabNotification; 